import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Input,
  NativeSelect,
  Separator,
  Tag,
  Text,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { capitalizeFirstLetter } from '@utils/formats';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';

const BEHAVIOUR_LABELS: Record<number, string> = {
  0: 'Finalizar atendimento',
  1: 'Desabilitar cliente',
  2: 'Aguardando resposta',
  4: 'Fechar contrato',
};

const NON_EDGE_BEHAVIOURS = [0, 1, 2, 4];

type DispositionOption = {
  id: string;
  name: string;
  behaviour: number;
};

interface Props {
  funnelId: string;
  funnelName: string;
  funnelOrder: number;
  onClose: () => void;
  onRenamed: (newName: string) => void;
  onReordered: (newOrder: number) => void;
  onDataChanged: () => void;
  onRequestDelete: () => void;
}

const NodePanel = ({ funnelId, funnelName, funnelOrder, onClose, onRenamed, onReordered, onDataChanged, onRequestDelete }: Props) => {
  const [name, setName] = useState(funnelName);
  const [order, setOrder] = useState(String(funnelOrder));
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [orderError, setOrderError] = useState('');

  const [linkedDispositions, setLinkedDispositions] = useState<DispositionOption[]>([]);
  const [allOptions, setAllOptions] = useState<DispositionOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [selectedBehaviour, setSelectedBehaviour] = useState<string>('');
  const [selectedOptionId, setSelectedOptionId] = useState<string>('');
  const [linking, setLinking] = useState(false);

  const loadData = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const [funnelRes, allRes] = await Promise.all([
        api.get(`/disposition_option/available`, { params: { funnelId } }),
        api.get('/disposition_option/available'),
      ]);
      const funnelOpts: DispositionOption[] = funnelRes.data ?? [];
      setLinkedDispositions(funnelOpts.filter((o) => NON_EDGE_BEHAVIOURS.includes(o.behaviour)));
      const all: DispositionOption[] = allRes.data ?? [];
      setAllOptions(all.filter((o) => NON_EDGE_BEHAVIOURS.includes(o.behaviour)));
    } catch (error) {
      datadogRum.addError(new Error('Fail to load node panel data'), { error, funnelId });
    } finally {
      setLoadingOptions(false);
    }
  }, [funnelId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveName = async () => {
    const trimmed = capitalizeFirstLetter(name.trim());
    if (!trimmed || trimmed.length < 2) {
      setNameError('Nome deve ter ao menos 2 caracteres.');
      return;
    }
    setNameError('');
    setSavingName(true);
    try {
      await api.put(`/sales_funnel/${funnelId}`, { name: trimmed });
      toast.success('Nome atualizado!');
      onRenamed(trimmed);
    } catch (error) {
      toast.error('Erro ao atualizar nome.');
      datadogRum.addError(new Error('Fail to rename funnel node'), { error, funnelId });
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveOrder = async () => {
    const parsed = parseInt(order, 10);
    if (!parsed || parsed < 1) {
      setOrderError('Ordem deve ser um número maior que 0.');
      return;
    }
    setOrderError('');
    setSavingName(true);
    try {
      await api.put(`/sales_funnel/${funnelId}`, { order: parsed });
      toast.success('Ordem atualizada!');
      onReordered(parsed);
    } catch (error) {
      toast.error('Erro ao atualizar ordem.');
      datadogRum.addError(new Error('Fail to reorder funnel node'), { error, funnelId });
    } finally {
      setSavingName(false);
    }
  };

  const availableToLink = allOptions.filter(
    (o) => !linkedDispositions.some((l) => l.id === o.id)
  );

  const filteredByBehaviour = selectedBehaviour
    ? availableToLink.filter((o) => String(o.behaviour) === selectedBehaviour)
    : availableToLink;

  const handleLink = async () => {
    if (!selectedOptionId) return;
    setLinking(true);
    try {
      await api.post(`/sales_funnel/${funnelId}/disposition/${selectedOptionId}`);
      setSelectedOptionId('');
      setSelectedBehaviour('');
      toast.success('Tabulação vinculada!');
      onDataChanged();
    } catch (error) {
      toast.error('Erro ao vincular tabulação.');
      datadogRum.addError(new Error('Fail to link disposition to funnel'), {
        error,
        funnelId,
        selectedOptionId,
      });
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = async (dispositionId: string) => {
    try {
      await api.delete(`/sales_funnel/${funnelId}/disposition/${dispositionId}`);
      toast.success('Tabulação desvinculada!');
      onDataChanged();
    } catch (error) {
      toast.error('Erro ao desvincular tabulação.');
      datadogRum.addError(new Error('Fail to unlink disposition from funnel'), {
        error,
        funnelId,
        dispositionId,
      });
    }
  };

  return (
    <Box
      position="absolute"
      top={0}
      right={0}
      w={320}
      h="100%"
      bg="grayscale.fg"
      borderLeft="1px solid"
      borderColor="border.muted"
      p={4}
      zIndex={10}
      overflowY="auto"
    >
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontWeight={700} fontSize="md">Etapa</Text>
        <CloseButton size="sm" onClick={onClose} />
      </Flex>

      <Text fontSize="sm" fontWeight={500} mb={1}>Nome</Text>
      <Flex gap={2} mb={1}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="subtle"
          size="sm"
          flex={1}
        />
        <Button size="sm" bg="green.700" color="white" onClick={handleSaveName} loading={savingName}>
          Salvar
        </Button>
      </Flex>
      {nameError && <ErrorLabel text={nameError} />}

      <Text fontSize="sm" fontWeight={500} mt={4} mb={1}>Ordem</Text>
      <Flex gap={2} mb={1}>
        <Input
          type="number"
          min={1}
          value={order}
          onChange={(e) => setOrder(e.target.value)}
          variant="subtle"
          size="sm"
          flex={1}
        />
        <Button size="sm" bg="green.700" color="white" onClick={handleSaveOrder} loading={savingName}>
          Salvar
        </Button>
      </Flex>
      {orderError && <ErrorLabel text={orderError} />}

      <Separator my={4} />

      <Text fontSize="sm" fontWeight={700} mb={2}>Tabulações da etapa</Text>

      {loadingOptions ? <Loading /> : (
        <>
          <Flex gap={1} flexWrap="wrap" mb={3}>
            {linkedDispositions.length === 0 && (
              <Text fontSize="xs" color="fg.muted">Nenhuma tabulação vinculada.</Text>
            )}
            {linkedDispositions.map((d) => (
              <Tag.Root key={d.id} size="md" variant="subtle" colorPalette="gray">
                <Tag.Label fontSize="xs">{d.name}</Tag.Label>
                <Tag.CloseTrigger onClick={() => handleUnlink(d.id)} />
              </Tag.Root>
            ))}
          </Flex>

          <Text fontSize="xs" fontWeight={500} mb={1}>Vincular tabulação</Text>
          <NativeSelect.Root size="sm" variant="subtle" mb={2}>
            <NativeSelect.Field
              placeholder="Filtrar por comportamento"
              value={selectedBehaviour}
              onChange={(e) => {
                setSelectedBehaviour(e.currentTarget.value);
                setSelectedOptionId('');
              }}
            >
              {NON_EDGE_BEHAVIOURS.map((b) => (
                <option key={b} value={String(b)}>{BEHAVIOUR_LABELS[b]}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <NativeSelect.Root size="sm" variant="subtle" mb={2}>
            <NativeSelect.Field
              placeholder="Selecione a tabulação"
              value={selectedOptionId}
              onChange={(e) => setSelectedOptionId(e.currentTarget.value)}
            >
              {filteredByBehaviour.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>

          <Button
            size="sm"
            bg="green.700"
            color="white"
            w="full"
            onClick={handleLink}
            loading={linking}
            disabled={!selectedOptionId}
          >
            Vincular
          </Button>
        </>
      )}

      <Separator my={4} />

      <Button
        size="sm"
        variant="outline"
        colorPalette="red"
        w="full"
        onClick={onRequestDelete}
      >
        Excluir etapa
      </Button>
    </Box>
  );
};

export default NodePanel;
